import {
  arrayOf,
  boolOf,
  fail,
  idOf,
  numberOf,
  objectOf,
  optionalBool,
  optionalNumber,
  optionalText,
  textOf,
} from '../../../shared/api/wire'
import type {
  AqsiRegisterCheck,
  AqsiRegisterCheckRun,
  AqsiTraceDeepState,
  AqsiTraceDetailEvent,
  AqsiTraceNetwork,
  AqsiTracePoint,
  AqsiTraceRegisterSettings,
  AqsiTraceSale,
  AqsiTraceSaleItem,
  AqsiTraceSales,
  TraceSegment,
  TraceSource,
} from '../model/types'

/**
 * Декодеры ответов `trace/*`. Ответ трассировки — обычный объект, без конверта кассовых ручек: это служебная
 * диагностика, а не команда кассе.
 */

const SOURCES: ReadonlySet<string> = new Set<TraceSource>(['browser', 'server', 'cabinet', 'register'])

function instantOf(value: unknown, field: string): string {
  const text = textOf(value, field)
  return Number.isNaN(Date.parse(text)) ? fail(`${field} — не дата`) : text
}

function optionalInstant(value: unknown, field: string): string | null {
  return value === null || value === undefined ? null : instantOf(value, field)
}

function msOf(value: unknown, field: string): number {
  const ms = numberOf(value, field)
  return ms >= 0 ? ms : fail(`${field} — отрицательная длительность`)
}

function decodeSegment(raw: unknown, field: string): TraceSegment {
  const item = objectOf(raw, field)
  return { key: idOf(item.key, `${field}.key`), label: textOf(item.label, `${field}.label`), ms: msOf(item.ms, `${field}.ms`) }
}

function decodeSegments(raw: unknown, field: string): readonly TraceSegment[] {
  return arrayOf(raw, field).map((item, index) => decodeSegment(item, `${field}[${index}]`))
}

function decodeSaleItem(raw: unknown, field: string): AqsiTraceSaleItem {
  const item = objectOf(raw, field)
  return {
    saleId: idOf(item.saleId, `${field}.saleId`),
    startedAt: instantOf(item.startedAt, `${field}.startedAt`),
    finishedAt: optionalInstant(item.finishedAt, `${field}.finishedAt`),
    outcome: textOf(item.outcome, `${field}.outcome`),
    totalMs: msOf(item.totalMs, `${field}.totalMs`),
    dominant: textOf(item.dominant, `${field}.dominant`),
    segments: decodeSegments(item.segments, `${field}.segments`),
  }
}

export function decodeTraceSales(raw: unknown): AqsiTraceSales {
  const data = objectOf(raw, 'список продаж трассировки')
  return { items: arrayOf(data.items, 'items').map((item, index) => decodeSaleItem(item, `items[${index}]`)) }
}

function decodePoint(raw: unknown, field: string): AqsiTracePoint {
  const item = objectOf(raw, field)
  const source = textOf(item.source, `${field}.source`)
  if (!SOURCES.has(source)) fail(`${field}.source — неизвестный источник ${source}`)
  return {
    source: source as TraceSource,
    stage: idOf(item.stage, `${field}.stage`),
    at: instantOf(item.at, `${field}.at`),
    durationMs: optionalNumber(item.durationMs, `${field}.durationMs`),
    outcome: optionalText(item.outcome, `${field}.outcome`),
  }
}

function decodeNetwork(raw: unknown): AqsiTraceNetwork | null {
  if (raw === null || raw === undefined) return null
  const item = objectOf(raw, 'network')
  return {
    effectiveType: optionalText(item.effectiveType, 'network.effectiveType'),
    rttMs: optionalNumber(item.rttMs, 'network.rttMs'),
    downlinkMbps: optionalNumber(item.downlinkMbps, 'network.downlinkMbps'),
  }
}

function decodeSettings(raw: unknown): AqsiTraceRegisterSettings | null {
  if (raw === null || raw === undefined) return null
  const item = objectOf(raw, 'registerSettings')
  return {
    acquiringSlipsCount: optionalNumber(item.acquiringSlipsCount, 'registerSettings.acquiringSlipsCount'),
    receiptCompactCheck: optionalBool(item.receiptCompactCheck, 'registerSettings.receiptCompactCheck'),
    hasAdvertisingLines: optionalBool(item.hasAdvertisingLines, 'registerSettings.hasAdvertisingLines'),
    acquiringLockFn: optionalBool(item.acquiringLockFn, 'registerSettings.acquiringLockFn'),
    isStandbyModeEnabled: optionalBool(item.isStandbyModeEnabled, 'registerSettings.isStandbyModeEnabled'),
    displayTurningOffTimeoutMillis: optionalNumber(
      item.displayTurningOffTimeoutMillis,
      'registerSettings.displayTurningOffTimeoutMillis',
    ),
    modeAcquiringTerminalEnable: optionalBool(item.modeAcquiringTerminalEnable, 'registerSettings.modeAcquiringTerminalEnable'),
    isCardQrCombined: optionalBool(item.isCardQrCombined, 'registerSettings.isCardQrCombined'),
  }
}

function decodeDetailEvent(raw: unknown, field: string): AqsiTraceDetailEvent {
  const item = objectOf(raw, field)
  return {
    kind: textOf(item.kind, `${field}.kind`),
    route: textOf(item.route, `${field}.route`),
    method: optionalText(item.method, `${field}.method`),
    at: instantOf(item.at, `${field}.at`),
    durationMs: optionalNumber(item.durationMs, `${field}.durationMs`),
    status: optionalNumber(item.status, `${field}.status`),
    attempt: optionalNumber(item.attempt, `${field}.attempt`),
    bytes: optionalNumber(item.bytes, `${field}.bytes`),
  }
}

/** Разбор продажи: продажа в ответе обязана быть той, что запрошена. */
export function decodeTraceSale(raw: unknown, saleId: string): AqsiTraceSale {
  const data = objectOf(raw, 'разбор продажи')
  const answered = idOf(data.saleId, 'saleId')
  if (answered.toLowerCase() !== saleId.toLowerCase()) fail('в ответе другая продажа, чем в запросе')
  return {
    saleId: answered,
    points: arrayOf(data.points, 'points').map((item, index) => decodePoint(item, `points[${index}]`)),
    segments: decodeSegments(data.segments, 'segments'),
    dominant: textOf(data.dominant, 'dominant'),
    network: decodeNetwork(data.network),
    registerSettings: decodeSettings(data.registerSettings),
    detail:
      data.detail === null || data.detail === undefined
        ? []
        : arrayOf(data.detail, 'detail').map((item, index) => decodeDetailEvent(item, `detail[${index}]`)),
  }
}

export function decodeTraceDeep(raw: unknown): AqsiTraceDeepState {
  const data = objectOf(raw, 'подробный режим')
  return { enabled: boolOf(data.enabled, 'enabled'), until: optionalInstant(data.until, 'until') }
}

function decodeRun(raw: unknown, field: string): AqsiRegisterCheckRun {
  const item = objectOf(raw, field)
  return {
    deliveryMs: optionalNumber(item.deliveryMs, `${field}.deliveryMs`),
    totalMs: optionalNumber(item.totalMs, `${field}.totalMs`),
    outcome: textOf(item.outcome, `${field}.outcome`),
  }
}

export function decodeRegisterCheck(raw: unknown): AqsiRegisterCheck {
  const data = objectOf(raw, 'замер кассы')
  return {
    runs: arrayOf(data.runs, 'runs').map((item, index) => decodeRun(item, `runs[${index}]`)),
    medianDeliveryMs: optionalNumber(data.medianDeliveryMs, 'medianDeliveryMs'),
  }
}
